import React, { createContext, useContext, useCallback } from 'react';

import __useMedia from './hook';

import { ViewportSize } from '../../../utils';

interface InterfaceContext {
    gt: (size: number) => boolean;
    gte: (size: number) => boolean;
    isFullMobile: boolean;
    isMobile: boolean;
    isMobileBig: boolean;
    isMobileMiddle: boolean;
    isMobileOrTablet: boolean;
    isTablet: boolean;
    lt: (size: number) => boolean;
    lte: (size: number) => boolean;
    viewport: ViewportSize;
}

export { ViewportSize };

const defaultInterfaceContext: InterfaceContext = {
    viewport: ViewportSize.PHONE_S,
    isMobile: true,
    isMobileBig: false,
    isMobileMiddle: false,
    isTablet: false,
    isMobileOrTablet: true,
    isFullMobile: true,
    lt: (size: number) => ViewportSize.PHONE_S < size,
    lte: (size: number) => ViewportSize.PHONE_S <= size,
    gt: (size: number) => ViewportSize.PHONE_S > size,
    gte: (size: number) => ViewportSize.PHONE_S >= size,
};

const InterfaceContext = createContext<InterfaceContext>(defaultInterfaceContext);

export const InterfaceProvider = ({ children }: React.PropsWithChildren<Record<string, unknown>>) => {
    const viewport = __useMedia();
    const isMobile = viewport === ViewportSize.PHONE_S;
    const isMobileMiddle = viewport === ViewportSize.PHONE_M;
    const isMobileBig = viewport === ViewportSize.PHONE_L;
    const isTablet = viewport === ViewportSize.TABLET;
    const isFullMobile = isMobile || isMobileMiddle || isMobileBig;
    const isMobileOrTablet = isMobile || isMobileBig || isTablet;

    const lt = useCallback((size: number) => viewport < size, [viewport]);
    const lte = useCallback((size: number) => viewport <= size, [viewport]);
    const gt = useCallback((size: number) => viewport > size, [viewport]);
    const gte = useCallback((size: number) => viewport >= size, [viewport]);

    return (
        <InterfaceContext.Provider
            value={{
                viewport,
                isMobile,
                isMobileBig,
                isMobileMiddle,
                isFullMobile,
                isTablet,
                isMobileOrTablet,
                lt,
                lte,
                gt,
                gte,
            }}
        >
            {children}
        </InterfaceContext.Provider>
    );
};

export const useMedia = () => useContext(InterfaceContext);
